import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Plus, Pencil, Trash2, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import AnnouncementEditor from "./AnnouncementEditor";
import { audienceLabel } from "./AnnouncementDialogs";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

export default function AnnouncementsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/announcements").then(({ data }) => setItems(data || [])).catch(err);
  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete the draft "${a.title}"?`)) return;
    try { await api.delete(`/admin/announcements/${a.id}`); toast.success("Draft deleted"); load(); } catch (e) { err(e); }
  };

  if (editing) {
    return (
      <AnnouncementEditor
        initial={editing === "new" ? null : editing}
        onClose={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-announcements-panel">
      <div className="card-base p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-heading text-lg font-semibold flex items-center gap-2">
            <Megaphone size={18} className="text-sage-600" /> Announcements
          </div>
          <p className="text-sm text-ink-600 mt-0.5">
            Write once, send in-app and by email. Nothing goes out until you confirm.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setEditing("new")} data-testid="announcement-new-btn">
          <Plus size={16} /> New announcement
        </button>
      </div>

      {items.length === 0 && <p className="text-ink-500 text-sm" data-testid="announcements-empty">No announcements yet.</p>}
      <ul className="space-y-2" data-testid="announcements-list">
        {items.map((a) => (
          <AnnouncementRow key={a.id} a={a} onEdit={() => setEditing(a)} onDelete={() => remove(a)} onChanged={load} />
        ))}
      </ul>
    </div>
  );
}

const STATUS_STYLE = {
  draft: "bg-ivory-100 text-ink-700",
  sending: "bg-amber-soft/60 text-amber-deep",
  sent: "bg-sage-50 text-sage-700",
};

function AnnouncementRow({ a, onEdit, onDelete, onChanged }) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);

  const loadReport = () => api.get(`/admin/announcements/${a.id}/deliveries`).then(({ data }) => setReport(data)).catch(err);
  const toggle = () => { if (!open) loadReport(); setOpen(!open); };
  const resume = async () => {
    try { await api.post(`/admin/announcements/${a.id}/resume`); toast.success("Sending resumed"); loadReport(); onChanged(); } catch (e) { err(e); }
  };

  return (
    <li className="card-base p-4" data-testid={`announcement-row-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-semibold truncate">{a.title}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[a.status]}`} data-testid={`announcement-status-${a.id}`}>
              {a.status}{a.email_dry_run ? " · email dry run" : ""}
            </span>
          </div>
          <div className="text-xs text-ink-500 mt-1">
            {audienceLabel(a.audience)} · {[a.send_in_app && "In-app", a.send_email && "Email"].filter(Boolean).join(" + ")}
            {a.sent_at ? ` · sent ${new Date(a.sent_at).toLocaleString()} by ${a.sent_by}` : ` · created ${new Date(a.created_at).toLocaleString()}`}
          </div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {a.status === "draft" ? (
            <>
              <button className="btn-secondary !py-1.5 !px-3 text-sm" onClick={onEdit} data-testid={`announcement-edit-${a.id}`}><Pencil size={14} /> Open</button>
              <button className="btn-secondary !py-1.5 !px-3 text-sm" onClick={onDelete} data-testid={`announcement-delete-${a.id}`}><Trash2 size={14} /></button>
            </>
          ) : (
            <button className="btn-secondary !py-1.5 !px-3 text-sm" onClick={toggle} data-testid={`announcement-report-${a.id}`}>
              Delivery {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
      </div>
      {open && report && <DeliveryReport report={report} onResume={resume} id={a.id} />}
    </li>
  );
}

function DeliveryReport({ report, onResume, id }) {
  const s = report.stats || {};
  const cells = [
    ["In-app delivered", s.in_app?.sent || 0],
    ["Banner dismissed", s.dismissed || 0],
    ["Emails sent", s.email?.sent || 0],
    ["Email dry run", s.email?.dry_run || 0],
    ["Emails failed", s.email?.failed || 0],
  ];
  return (
    <div className="mt-3 pt-3 border-t border-ivory-200 space-y-3" data-testid={`announcement-delivery-${id}`}>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {cells.map(([k, v]) => (
          <div key={k} className="rounded-xl bg-ivory-100 p-2.5">
            <div className="font-heading text-xl font-bold">{v}</div>
            <div className="text-xs text-ink-600">{k}</div>
          </div>
        ))}
      </div>
      {report.status === "sending" && !report.running && (
        <button className="btn-secondary text-sm" onClick={onResume} data-testid={`announcement-resume-${id}`}>
          <RotateCcw size={14} /> Resume sending (already-delivered people are skipped)
        </button>
      )}
      {report.failures?.length > 0 && (
        <ul className="text-xs text-terracotta-600 space-y-0.5">
          {report.failures.map((f) => <li key={f.user_id}>{f.email}: {f.error}</li>)}
        </ul>
      )}
    </div>
  );
}
